import { useState, type FormEvent } from "react";
import { ArrowLeft, MailCheck } from "lucide-react";
import { emailPrefixSchema } from "@ubisafe/shared";
import { BrandLogo } from "../components/BrandLogo";
import { api, ApiClientError } from "../lib/api";

interface ForgotPasswordPageProps {
  onBack: () => void;
}

export function ForgotPasswordPage({ onBack }: ForgotPasswordPageProps) {
  const [prefix, setPrefix] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const parsed = emailPrefixSchema.safeParse(prefix);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Informe seu usuário.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.forgotPassword(parsed.data);
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível solicitar a recuperação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout auth-layout--compact">
      <section className="auth-hero">
        <BrandLogo />
        <div className="auth-hero__content">
          <span className="eyebrow">Recuperação de acesso</span>
          <h1>Retome o acesso com segurança.</h1>
          <p>As instruções são enviadas somente ao endereço corporativo cadastrado.</p>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-card__icon" aria-hidden="true"><MailCheck /></div>
          <span className="eyebrow eyebrow--dark">Redefinição</span>
          <h2>Esqueci minha senha</h2>
          <p className="form-intro">Informe o seu usuário para receber um link temporário.</p>

          {message ? (
            <>
              <div className="form-alert form-alert--success" role="status">{message}</div>
              <button className="secondary-button secondary-button--full" type="button" onClick={onBack}>
                <ArrowLeft aria-hidden="true" /> Voltar para o login
              </button>
            </>
          ) : (
            <form onSubmit={(event) => void submit(event)} noValidate>
              <label htmlFor="forgot-prefix">Usuário</label>
              <div className="suffix-input">
                <input
                  id="forgot-prefix"
                  value={prefix}
                  onChange={(event) => setPrefix(event.target.value.toLowerCase().replace("@", ""))}
                  autoComplete="username"
                  autoCapitalize="none"
                  placeholder="seu.nome"
                  maxLength={64}
                />
                <span>@ubisafe.com.br</span>
              </div>
              {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}
              <button className="primary-button primary-button--full" type="submit" disabled={loading}>
                {loading ? "Enviando…" : "Enviar instruções"}
              </button>
              <button className="link-button" type="button" onClick={onBack}>
                <ArrowLeft aria-hidden="true" /> Voltar para o login
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
